import { Column, Table } from "react-migration/components/TableDeprecated";
import type { EnrichedSlopeCategory, SlopeCategory, TopographySelectionSidebarInnerProps } from "./types";
import { useEffect, useMemo, useState } from "react";

import { Hatch } from "react-migration/domains/constraints/components/Hatch/Hatch";
import { SLOPE_CATEGORIES } from "./constants";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { SlopeStatsStrict } from "./Slope/types";
import { Sparkline } from "react-migration/components/Sparkline/Sparkline";
import { useSlopeStatsQuery } from "./Slope/useSlopeStatsQuery";

const SLOPE_COLUMNS = [
  {
    label: "Category",
    grow: false,
    render: ({ color, name }) => (
      <div className="flex gap-2">
        <Hatch borderColor={color} fillColor={color} />
        <span>{name}</span>
      </div>
    ),
  },
  {
    label: "Gradient",
    render: ({ description }) => <span>{description}</span>,
  },
  {
    label: "Coverage",
    render: ({ percentage }) => <span>{percentage}%</span>,
  },
] as Column<EnrichedSlopeCategory>[];

export function SlopeSummary({
  selection,
  onLoaded,
  onStatsLoaded,
}: TopographySelectionSidebarInnerProps<SlopeStatsStrict>) {
  const {
    data: slopeStats,
    loading: loadingSlopeStats,
    error: slopeError,
  } = useSlopeStatsQuery(selection.feature?.geometry, {
    onLoaded,
  });

  useEffect(() => {
    if (slopeStats) {
      onStatsLoaded?.(slopeStats);
    }
  }, [slopeStats, onStatsLoaded]);

  const [activeCategory, setActiveCategory] = useState<SlopeCategory | undefined>();

  const categoryChanging = (row: SlopeCategory | undefined) => {
    setActiveCategory(row);
  };

  const mappedSlopeStats = useMemo(() => {
    const slopeResults = slopeStats ?? [];

    return SLOPE_CATEGORIES.map((category) => {
      const slopeCategoryResult = slopeResults.find((slopeResult) => slopeResult?.category === category.categoryKey);

      return {
        percentage: slopeCategoryResult?.percentage ?? 0,
        ...category,
      } as EnrichedSlopeCategory;
    });
  }, [slopeStats]);

  if (slopeError) {
    // Caught by boundary above
    throw slopeError;
  }

  if (loadingSlopeStats) {
    return (
      <div className="relative h-[324px] w-full">
        <SkeletonLoading rows={1} showPadding={false} />
      </div>
    );
  }

  return (
    <>
      <Sparkline categories={mappedSlopeStats} hoveredCategoryKey={activeCategory?.categoryKey} />
      <Table
        primaryKey="categoryKey"
        columns={SLOPE_COLUMNS}
        rows={mappedSlopeStats}
        size="small"
        onRowHover={(row) => categoryChanging(row)}
      />
    </>
  );
}
